import {
  Calendar,
  ChevronRight,
  Clock,
  Receipt,
  Search,
  UserPlus,
  Users,
} from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/lib/utils';
import type {
  GlobalSearchActionHit,
  GlobalSearchRecentHit,
} from '../domain/global-search.types';
import { GLOBAL_SEARCH_MIN_CHARS } from '../domain/global-search.types';

const ACTION_ICONS = {
  userPlus: UserPlus,
  users: Users,
  calendar: Calendar,
  receipt: Receipt,
} as const;

const RECENT_KIND_LABEL: Record<GlobalSearchRecentHit['entityKind'], string> = {
  patient: 'Patient',
  appointment: 'Appointment',
  invoice: 'Invoice',
};

interface GlobalSearchIdleProps {
  recents: GlobalSearchRecentHit[];
  actions: GlobalSearchActionHit[];
  highlightIndex: number;
  onHighlight: (index: number) => void;
  onSelect: (href: string) => void;
  onClearRecents: () => void;
  listId: string;
}

/** Idle palette body — recents + quick-action tiles. */
export function GlobalSearchIdleState({
  recents,
  actions,
  highlightIndex,
  onHighlight,
  onSelect,
  onClearRecents,
  listId,
}: GlobalSearchIdleProps): JSX.Element {
  const actionOffset = recents.length;

  return (
    <div className="px-3.5 pb-3.5 pt-2">
      <div className="mb-3 rounded-xl border border-surface-200/80 bg-surface-50/80 px-3 py-2.5">
        <p className="text-[11px] font-medium text-ink-500">
          Try{' '}
          <kbd className="rounded border border-surface-200 bg-white px-1 font-mono text-[10px]">
            p Mehta
          </kbd>{' '}
          or{' '}
          <kbd className="rounded border border-surface-200 bg-white px-1 font-mono text-[10px]">
            &gt; book
          </kbd>
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(
            [
              { prefix: 'p', label: 'Patients' },
              { prefix: 'a', label: 'Appointments' },
              { prefix: 'i', label: 'Invoices' },
              { prefix: '>', label: 'Commands' },
            ] as const
          ).map((hint) => (
            <span
              key={hint.prefix}
              className="inline-flex items-center gap-1 rounded-full border border-surface-200 bg-white px-2 py-0.5 text-[10px] font-medium text-ink-600"
            >
              <kbd className="font-mono text-[10px] text-brand-600">{hint.prefix}</kbd>
              {hint.label}
            </span>
          ))}
        </div>
      </div>
      {recents.length > 0 ? (
        <div className="mb-4">
          <div className="mb-1.5 flex items-center justify-between px-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
              Recent
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto px-1.5 py-0.5 text-[11px] font-medium text-ink-400 hover:text-ink-700"
              onClick={onClearRecents}
            >
              Clear
            </Button>
          </div>
          <div className="space-y-0.5" role="group" aria-label="Recent destinations">
            {recents.map((recent, index) => {
              const active = index === highlightIndex;
              return (
                <Button
                  key={recent.id}
                  type="button"
                  variant="ghost"
                  role="option"
                  id={`global-search-hit-${index}`}
                  aria-selected={active}
                  className={cn(
                    'global-search-option h-auto w-full justify-start gap-2.5 rounded-xl px-2.5 py-2 text-left',
                    active && 'global-search-option-active',
                  )}
                  onMouseEnter={() => onHighlight(index)}
                  onClick={() => onSelect(recent.href)}
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-100 text-ink-500">
                    <Clock className="size-3.5" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-ink-900">
                      {recent.label}
                    </span>
                    <span className="text-[11px] text-ink-500">
                      {RECENT_KIND_LABEL[recent.entityKind]}
                    </span>
                  </span>
                  <ChevronRight
                    className={cn(
                      'size-3.5 shrink-0 text-ink-300',
                      active && 'text-brand-600',
                    )}
                    aria-hidden
                  />
                </Button>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="mb-3 px-1 text-[12px] leading-relaxed text-ink-500">
          Search across this clinic, or run a command with &gt;
        </p>
      )}

      <div className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
        Quick actions
      </div>
      <div
        id={listId}
        role="listbox"
        aria-label="Quick actions"
        className="grid gap-2 sm:grid-cols-2"
      >
        {actions.map((action, index) => {
          const Icon = ACTION_ICONS[action.icon];
          const flatIndex = actionOffset + index;
          const active = flatIndex === highlightIndex;
          return (
            <Button
              key={action.id}
              type="button"
              variant="ghost"
              role="option"
              id={`global-search-hit-${flatIndex}`}
              aria-selected={active}
              className={cn(
                'global-search-action-tile h-auto flex-col items-stretch gap-2 rounded-xl border border-surface-200 bg-surface-50/80 px-3 py-3 text-left hover:bg-brand-50/50',
                active && 'global-search-option-active border-brand-200 bg-brand-50/80',
              )}
              onMouseEnter={() => onHighlight(flatIndex)}
              onClick={() => onSelect(action.href)}
            >
              <span className="flex size-9 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
                <Icon className="size-4" aria-hidden />
              </span>
              <span className="flex items-start justify-between gap-1">
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-ink-900">{action.label}</span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-ink-500">
                    {action.description}
                  </span>
                </span>
                <ChevronRight
                  className={cn(
                    'mt-0.5 size-3.5 shrink-0 text-ink-300 transition-all',
                    active ? 'translate-x-0.5 text-brand-600' : 'opacity-0 group-hover:opacity-100',
                  )}
                  aria-hidden
                />
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

/** Shown while the query is shorter than the network search threshold. */
export function GlobalSearchMinCharsHint({ query }: { query: string }): JSX.Element {
  const remaining = Math.max(GLOBAL_SEARCH_MIN_CHARS - query.trim().length, 0);
  return (
    <div className="flex flex-col items-center px-4 py-8 text-center">
      <div className="mb-3 flex size-11 items-center justify-center rounded-2xl bg-surface-100">
        <Search className="size-4 text-ink-400" aria-hidden />
      </div>
      <p className="text-[13px] font-semibold text-ink-800">Keep typing…</p>
      <p className="mt-1 max-w-[280px] text-[12px] leading-relaxed text-ink-500">
        Enter at least {GLOBAL_SEARCH_MIN_CHARS} characters
        {remaining > 0 ? ` (${remaining} more)` : ''} after a prefix like{' '}
        <kbd className="rounded border border-surface-200 bg-white px-1 font-mono text-[10px]">p</kbd>{' '}
        to search.
      </p>
    </div>
  );
}

interface GlobalSearchNoResultsProps {
  query: string;
  onRegisterPatient: () => void;
}

/** Empty results state with register CTA. */
export function GlobalSearchNoResults({
  query,
  onRegisterPatient,
}: GlobalSearchNoResultsProps): JSX.Element {
  return (
    <div className="flex flex-col items-center px-4 py-10 text-center">
      <div className="mb-3 flex size-12 items-center justify-center rounded-2xl bg-surface-100">
        <Search className="size-5 text-ink-400" aria-hidden />
      </div>
      <p className="text-[13px] font-semibold text-ink-800">No matches for “{query}”</p>
      <p className="mt-1 max-w-[280px] text-[12px] leading-relaxed text-ink-500">
        Try a name, mobile, patient ID, appointment time, or invoice number
      </p>
      <Button
        variant="default"
        size="sm"
        className="mt-4 gap-1.5 rounded-lg"
        type="button"
        onClick={onRegisterPatient}
      >
        <UserPlus className="size-3.5" aria-hidden />
        Register patient
      </Button>
    </div>
  );
}

/** Skeleton rows shown while the first search response is in flight. */
export function GlobalSearchSkeleton(): JSX.Element {
  return (
    <div className="space-y-1 p-2" aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl px-2.5 py-2.5">
          <div className="global-search-skel size-10 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="global-search-skel h-3 w-2/5 rounded-md" />
            <div className="global-search-skel h-2.5 w-3/5 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
}
